import styles from './CareersListingModule.scss';

import cx from 'classnames';
import { ReferencedEntry } from 'contentful';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps, withRouter } from 'react-router';
import Select from 'react-select';
import Option, { OptionProps } from 'react-select/lib/components/Option';

import { DynamicAssembly } from 'src/api/contentful/contentTypes.cloverdotcom';
import { CategoryOption } from 'src/api/greenhouse/JobBoards';
import { CareersListings } from 'src/components/CareersListingModule/CareersListings/CareersListings';
import { getField, isEntry } from 'src/utils/EntryUtil';
import { generateId } from 'src/utils/IdGeneration';

import { BusyLoader } from '../BusyLoader/BusyLoader';
import { Text } from '../Contentful/Text';
import { useGreenhouse } from './UseGreenhouse';

interface ICareersListingModuleProps extends RouteComponentProps, React.AllHTMLAttributes<HTMLElement> {
  entry?: ReferencedEntry<DynamicAssembly>;
}

export const CustomOption: React.FC<OptionProps<CategoryOption>> = ({ data, isSelected, className, children, ...props }) => {
  const categoryOption = data as CategoryOption;
  return (
    <Option
      className={cx(className, styles.option, {
        [styles.optionParent]: categoryOption.isParent,
        [styles.optionSelected]: isSelected,
      })}
      isSelected={isSelected}
      data={data}
      {...props}
    >
      <span style={{ paddingLeft: categoryOption.level * 10 }}>
        {children}
      </span>
    </Option>
  );
};

const CareersListingModule: React.FunctionComponent<ICareersListingModuleProps> = ({
  entry,
  className,
  history,
  location,
  match,
  staticContext,
  ...props
}) => {
  const { t } = useTranslation();

  if (!isEntry(entry)) return null;

  const {
    optionsLoaded,
    categories,
    departments,
    offices,
    selectedDepartment,
    selectedOffice,
    selectDepartment,
    selectOffice,
  } = useGreenhouse(t, history);

  // determines if the device has touch enabled
  const isTouchEnabled = 'ontouchstart' in window || navigator.msMaxTouchPoints;

  const id = generateId(getField(entry, 'title', ''));

  return (
    <section className={cx(styles.moduleWrapper, className)} {...props}>
      <div className={styles.careersWrapper}>
        <Text id={id} entry={entry} field="title" tagName="h2" className={styles.careersTitle} />
        {!optionsLoaded && <BusyLoader className={styles.busyLoader} />}
        {optionsLoaded && (
          <>
            <div className={styles.dropdownContainer}>
              <Select
                components={{ Option: CustomOption }}
                aria-label={t('all-departments')}
                options={departments}
                onChange={selectDepartment}
                value={selectedDepartment}
                isSearchable={!isTouchEnabled}
                classNamePrefix={'react-select'}
              />
              <Select
                components={{ Option: CustomOption }}
                aria-label={t('all-locations')}
                options={offices}
                onChange={selectOffice}
                value={selectedOffice}
                isSearchable={!isTouchEnabled}
                classNamePrefix={'react-select'}
              />
            </div>
            <CareersListings categories={categories} />
          </>
        )}
      </div>
    </section>
  );
};

const Routed = withRouter(CareersListingModule);
type Routed = typeof Routed;

export {
  Routed as CareersListingModule
};
